import React, { useEffect, useState } from 'react';
import CloseIcon from '../../images/CloseIcon';
import { deleteProductsBulk } from '../../services/items.services';
import { showDeleteToast, showToast } from '../../services/toast.service';
import { showApiError } from '../../services/apiError';
import { BORDER_STRONG, DESTRUCTIVE, TEXT, TEXT_DIM, t } from './itemsListShared';

interface BulkDeleteDialogProps {
  isOpen: boolean;
  onClose: () => void;
  /** Called after a successful (or partially successful) delete. */
  onDeleted: () => void;
  /** The full product records behind the current selection. */
  products: any[];
  language: any;
  isMobile: boolean;
}

/**
 * Server skip reasons mapped to copy the user can act on. The API reports
 * `skipped: [{ id, reason }]` where `reason` is the message of the refusal
 * (ProductService.removeProductsBulk) — a missing or other-company id comes
 * back as "Product not found". Anything unmapped is shown verbatim.
 */
const REASON_COPY: Record<string, string> = {
  'Product not found': 'they no longer exist or belong to another company',
};

/**
 * Bulk delete over a selection. Same shell and copy pattern as the single-row
 * Delete dialog on the Items list, with the count in the title.
 */
const BulkDeleteDialog: React.FC<BulkDeleteDialogProps> = ({
  isOpen,
  onClose,
  onDeleted,
  products,
  language,
  isMobile,
}) => {
  const [deleting, setDeleting] = useState<boolean>(false);

  useEffect(() => {
    if (isOpen) setDeleting(false);
  }, [isOpen]);

  const close = () => {
    if (!deleting) onClose();
  };

  const confirm = async () => {
    if (deleting || products.length === 0) return;
    setDeleting(true);
    try {
      const response: any = await deleteProductsBulk({
        ids: products.map((p: any) => String(p.id)),
      });
      // POST /products/delete-bulk → data: { deleted: string[], skipped: [{ id, reason }] }
      const payload: any = response?.data?.data ?? response?.data ?? {};
      const deletedItems: any[] = Array.isArray(payload?.deleted) ? payload.deleted : [];
      const skippedItems: any[] = Array.isArray(payload?.skipped) ? payload.skipped : [];
      const deleted = deletedItems.length;
      const skipped = skippedItems.length;

      if (skipped > 0) {
        const reasons = Array.from(
          new Set(skippedItems.map((s: any) => String(s?.reason ?? '')).filter(Boolean))
        );
        const reasonText = reasons
          .map(code => t(language, REASON_COPY[code] ?? code, REASON_COPY[code] ?? code))
          .join('; ');
        showToast({
          message:
            deleted === 0
              ? t(language, 'No items were deleted', 'No items were deleted')
              : t(language, 'Items partly deleted', 'Items partly deleted'),
          desc: `<p class='font-inter text-[14px] text-main leading-6'>${deleted} ${t(
            language,
            'deleted',
            'deleted'
          )} · ${skipped} ${t(language, 'skipped', 'skipped')}${
            reasonText ? ` — ${reasonText}` : ''
          }</p>`,
          type: 'warning',
          duration: 10000,
        });
      } else {
        showDeleteToast(t(language, 'Items Deleted !', 'Items Deleted !'));
      }
      onClose();
      onDeleted();
    } catch (error: any) {
      // Unexpected server errors (5xx) stop the batch. The API returns the
      // ids it already deleted so the operator is not told "nothing happened"
      // about items that are gone.
      const body: any = error?.response?.data ?? {};
      const done: any[] = Array.isArray(body?.deleted) ? body.deleted : [];
      if (done.length > 0) {
        showToast({
          message: t(language, 'Items partly deleted', 'Items partly deleted'),
          desc: `<p class='font-inter text-[14px] text-main leading-6'>${done.length} ${t(language, 'deleted', 'deleted')}</p>`,
          type: 'warning',
          duration: 10000,
        });
        onClose();
        onDeleted();
      }
      showApiError(error, { language, titleFallback: t(language, 'Failed to delete items', 'Failed to delete items') });
    } finally {
      setDeleting(false);
    }
  };

  const panelStyle: React.CSSProperties = isMobile
    ? {
        width: '100%',
        backgroundColor: '#FFFFFF',
        borderRadius: '22px 22px 0 0',
        padding: '8px 20px 20px',
        boxShadow: '0 -12px 40px rgba(0,0,0,.12)',
        transition: 'transform 0.3s ease, opacity 0.3s ease',
      }
    : {
        width: '530px',
        backgroundColor: '#FFFFFF',
        borderRadius: '24px',
        padding: '24px',
        transition: 'transform 0.3s ease, opacity 0.3s ease',
      };

  const countLabel =
    products.length === 1 ? t(language, 'item?', 'item?') : t(language, 'items?', 'items?');

  return (
    <div
      className={`fixed top-0 left-0 flex ${
        isMobile ? 'items-end' : 'items-center px-4'
      } justify-center ${
        isOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
      } transition-opacity duration-300 ease-in-out`}
      style={{
        backgroundColor: '#22252552',
        zIndex: 200,
        visibility: isOpen ? 'visible' : 'hidden',
        transition: 'opacity 0.3s ease, visibility 0.3s ease',
        width: '100vw',
        height: '100vh',
      }}
    >
      <div
        className={`transform transition-transform duration-300 ease-in-out ${
          isOpen ? 'translate-y-0' : 'translate-y-4 opacity-0'
        }`}
        style={panelStyle}
      >
        {isMobile && (
          <div className="flex justify-center" style={{ padding: '2px 0 10px' }}>
            <div
              style={{
                width: '40px',
                height: '4px',
                borderRadius: '999px',
                backgroundColor: '#DBE4E4',
              }}
            />
          </div>
        )}
        <div className="flex items-center justify-between">
          <p
            style={{ fontSize: '20px', color: TEXT }}
            className="font-semibold font-inter leading-[24px]"
          >
            {t(language, 'Delete', 'Delete')} {products.length} {countLabel}
          </p>
          <div
            onClick={close}
            className="flex items-center justify-center cursor-pointer"
            style={{ width: '24px', height: '24px' }}
          >
            <CloseIcon />
          </div>
        </div>
        <div className="flex items-center justify-between" style={{ marginTop: '12px' }}>
          <p className="text-[14px] font-inter leading-[24px]" style={{ color: TEXT_DIM }}>
            {t(
              language,
              'These items will be permanently deleted. Are you sure you want to continue',
              'These items will be permanently deleted. Are you sure you want to continue'
            )}
          </p>
        </div>
        <div className="flex items-center justify-end" style={{ marginTop: '24px', gap: '12px' }}>
          <div
            onClick={close}
            className="cursor-pointer text-[14px] leading-[22px] font-inter font-medium"
            style={{
              borderRadius: '40px',
              padding: '10px 24px',
              border: `1px solid ${BORDER_STRONG}`,
              color: TEXT,
              opacity: deleting ? 0.5 : 1,
              pointerEvents: deleting ? 'none' : 'auto',
            }}
          >
            {t(language, 'Cancel', 'Cancel')}
          </div>
          <div
            onClick={confirm}
            className="text-[14px] leading-[22px] font-inter font-medium"
            style={{
              borderRadius: '40px',
              padding: '10px 24px',
              backgroundColor: deleting ? '#9CA3AF' : DESTRUCTIVE,
              color: '#FFFFFF',
              cursor: deleting ? 'not-allowed' : 'pointer',
            }}
          >
            {deleting
              ? t(language, 'Deleting...', 'Deleting...')
              : t(language, 'Delete', 'Delete')}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BulkDeleteDialog;
